/**
 * Work-scope keys for long-running tasks.
 *
 * These MUST match `TaskScopeKey` on the server (SuperTacho.Application/Requests/
 * TaskScopeKey.cs) — the server computes the authoritative key when it creates the
 * task, and the client computes the same string only to answer "is this scope already
 * busy?" when deciding whether to disable a button.
 *
 * The client copy is a convenience, never a guarantee: two browsers can both decide a
 * scope is free in the same millisecond. The guarantee is the filtered unique index
 * behind the server's ScopeKey column.
 *
 * Granularity is per period, not per selected drivers — the collision that matters is
 * two people recalculating the same month over overlapping drivers, which a key built
 * from exact driver ids would happily let through.
 */

const formatMonths = (months: number[] | undefined | null): string => {
  if (!months?.length) return ''
  return Array.from(new Set(months))
    .sort((a, b) => a - b)
    .join(',')
}

// UTC, nie czas lokalny: date bierze sie z wartosci wyslanej na serwer, ktory
// formatuje ja po zdeserializowaniu. Dla granicy okresu (np. 2026-06-30T22:00:00Z,
// czyli 1 lipca w Warszawie) obie strony policzylyby inny miesiac, wiec useScopeBusy
// nie zablokowalby przycisku i uzytkownik wpadalby dopiero w 409.
const yearMonth = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`

// Serwer formatuje Guid malymi literami; trasa moze niesc wielkie.
const normalizeCompanyId = (companyId: string) => companyId.toLowerCase()

export const taskScopeKey = {
  timesheet: (companyId: string, year: number, months: number[]) =>
    `ts:${normalizeCompanyId(companyId)}:${year}:${formatMonths(months)}`,

  timesheetRemoval: (companyId: string, year: number, months: number[]) =>
    `tsdel:${normalizeCompanyId(companyId)}:${year}:${formatMonths(months)}`,

  businessTripGeneration: (companyId: string, period: Date) =>
    `bt:${normalizeCompanyId(companyId)}:${yearMonth(period)}`,

  businessTripSave: (companyId: string, period: Date) =>
    `bts:${normalizeCompanyId(companyId)}:${yearMonth(period)}`,

  businessTripCostsRefresh: (companyId: string) => `btc:${normalizeCompanyId(companyId)}`,

  violations: (companyId: string) => `vi:${normalizeCompanyId(companyId)}`,
}
